/*
 * SAML peer area (audit Phase 3.3). Replaces wwwroot/js/SamlPeer/List.js,
 * Detail.js and _namespace.js on the Phase 3.1 core. No jQuery.
 *
 * One screen: the peers of a domain, and a detail dialog that is four editors
 * stacked in one body -- the peer's own fields (saved with Save), the service
 * provider details the IdP's administrator needs (read-only), the application
 * bindings, and the attribute mappings. The last two write per row and take
 * effect immediately, so they re-fetch the dialog rather than waiting for Save.
 *
 * Reach is granted, never inferred. An enabled peer bound to nothing is
 * silently dead, and the console says so at every point where that can happen:
 * the list's red "nowhere" badge, the create form's note, the detail banner, and
 * the unbind confirmation, which has to describe what removing THIS row does.
 * That is why a per-row write reloads the dialog: the warnings are rendered
 * server-side from the saved state, so a stale dialog would state the reach
 * wrongly rather than merely showing an old row.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'SamlPeerModal';
const CREATE_FORM = 'SamlPeerCreateForm';
const EDIT_FORM = 'SamlPeerEditForm';
const CREATE_ALERT = 'SamlPeerCreateMessageContainer';
const EDIT_ALERT = 'SamlPeerEditMessageContainer';

function currentDomainId(): number {
    const raw = (one('#CurrentDomainId') as HTMLInputElement | null)?.value ?? '';
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/*
 * A binding or a mapping written inside the dialog changes the row behind it --
 * the "Offered to" badge is a count of bindings, and it was rendered when the
 * list was. Reloading the list there is not an option: it CONTAINS the modal
 * placeholder, so the swap would tear the open dialog out of the page. Mark it
 * and redraw when the dialog closes, the same way the Feature area does.
 */
let listDirty = false;
let boundHide = false;

/** Install the one document-level listener this area needs. Called from main.ts. */
export function initSamlPeerArea(): void {
    if (boundHide) return;
    boundHide = true;
    document.addEventListener('hidden.bs.modal', event => {
        const node = event.target;
        if (!(node instanceof Element) || node.id !== MODAL) return;
        if (!listDirty) return;
        listDirty = false;
        void List.Reload();
    });
}

export const List = {
    ShowDetail(peerId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `SamlPeer/Detail/${peerId}`,
            title: null,
            // Naming the form is what attaches validation to it. Without this
            // the edit form was never parsed, so isValid() built a validator
            // with no rules and answered true for everything -- an empty Entity
            // ID reached the Api as a 400 rather than stopping at the field.
            formId: EDIT_FORM,
            errorTitle: `SAML Peer ${peerId}`,
            errorMessage: 'Peer Detail failed to execute:',
        });
    },

    NewPeer(domainId?: unknown): void {
        const parsed = Number.parseInt(String(domainId ?? ''), 10);
        const target = Number.isFinite(parsed) && parsed > 0 ? parsed : currentDomainId();
        if (!target) {
            toast('danger', 'Pick a domain first', 'A peer belongs to one domain; the rail\'s picker decides which.');
            return;
        }
        void ajaxModal({
            modal: MODAL,
            url: `SamlPeer/Create/${target}`,
            formId: CREATE_FORM,
            focusId: 'Peer_EntityId',
            errorTitle: 'New SAML Peer',
            errorMessage: 'Failed to render the new peer form',
        });
    },

    NewPeerSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const working = saving();
        working(true);
        void ajaxJson({
            url: 'SamlPeer',
            method: 'POST',
            data: serialize(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create SAML peer',
            onSuccess: () => {
                toast('success', 'SAML peer created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The form, not the alert div beside it: setValidationModelState
                // renders the model-level ('') entry into the container's own
                // div.validation-summary, and only the form has one.
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create SAML peer', summaryOf(data));
                return true;
            },
            onComplete: () => working(false),
        });
    },

    Reload(): Promise<void> {
        const domainId = currentDomainId();
        if (!domainId) return Promise.resolve();
        return ajaxSwap({
            container: 'ViewContainer',
            url: `SamlPeer/Domain/${domainId}`,
            alertTitle: 'Failed to reload SAML peers',
        });
    },
};

export const Detail = {
    /**
     * Copy one of the read-only SP fields to the clipboard. The console is
     * always a secure context, so navigator.clipboard is there; the
     * select-and-execCommand fallback keeps the button from silently doing
     * nothing if that ever stops being true.
     */
    CopyValue(inputId: string): void {
        const input = document.getElementById(inputId) as HTMLInputElement | null;
        if (!input) return;
        const value = input.value ?? '';
        const failed = (): void => toast('danger', 'Could not copy', 'Select the text and copy it manually.');
        const done = (): void => toast('success', 'Copied to clipboard');
        if (navigator.clipboard?.writeText) {
            void navigator.clipboard.writeText(value).then(done, failed);
            return;
        }
        input.select();
        input.setSelectionRange(0, value.length);
        if (document.execCommand?.('copy')) done(); else failed();
    },

    Save(peerId: number | string): void {
        const form = one(`#${EDIT_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        void ajaxJson({
            url: `SamlPeer/${peerId}`,
            method: 'PUT',
            data: serialize(form),
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to save peer',
            onSuccess: () => {
                toast('success', 'SAML peer saved');
                listDirty = false;
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${EDIT_FORM}`);
                toast('danger', 'Failed to save peer', summaryOf(data));
                return true;
            },
        });
    },

    Delete(peerId: number | string): void {
        void confirm({
            title: 'Delete SAML Peer',
            html: 'Delete this SAML peer?<br/><br/>'
                + '<span class="text-danger">Any sign-in flow that depends on it starts failing immediately.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `SamlPeer/${peerId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to delete peer',
                    onSuccess: () => {
                        toast('success', 'SAML peer deleted');
                        listDirty = false;
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete peer'),
                });
            },
        });
    },

    /**
     * Live mirror of the domain-wide checkbox. The consequence -- every
     * application, including ones added later -- is not visible in the label,
     * and the box is not persisted until Save, so this only reveals the
     * warning; it never claims the grant is in effect.
     *
     * `this` is the checkbox: core/dispatch fires a data-action on CHANGE for a
     * checkbox, not on click.
     */
    ToggleDomainWide(this: Element): void {
        const warning = one('#SamlPeerDomainWideWarning');
        warning?.classList.toggle('d-none', !(this as HTMLInputElement).checked);
    },

    /**
     * Bind an application. Reach is granted, never inferred, so the toast says
     * what the binding actually achieved -- which is nothing at all while the
     * domain-wide flag is set, and everything when it is the first one.
     * Both branches read the flag the SERVER echoed back rather than the
     * checkbox above, which may be holding an unsaved edit.
     */
    AddApplication(peerId: number | string): void {
        const applicationId = Number.parseInt((one('#SamlPeerAddAppId') as HTMLInputElement | null)?.value ?? '', 10);
        if (!Number.isFinite(applicationId) || applicationId <= 0) {
            toast('warning', 'Pick an application', 'Use the search button to pick one before pressing Bind.');
            return;
        }
        void ajaxJson({
            url: `SamlPeer/${peerId}/Application/${applicationId}`,
            method: 'POST',
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to bind application',
            onSuccess: data => {
                let detail = '';
                if (data['domainWide'] === true) {
                    detail = 'This peer is flagged domain-wide, so the binding has no effect until you untick that box.';
                } else if (data['boundCount'] === 1) {
                    detail = 'This peer is now offered on that application only.';
                }
                toast('success', 'Application bound', detail);
                reloadDialog(peerId);
            },
            onFailure: failToast('Failed to bind application'),
        });
    },

    RemoveApplication(peerId: number | string, applicationId: number | string): void {
        const table = one('#SamlPeerApplicationTable');
        const isLast = (table?.querySelectorAll('tbody tr[data-app-id]').length ?? 0) === 1;
        // .attr(), not .data(): this carries the SAVED flag, and the warning has
        // to describe what removing the row does right now.
        const domainWide = table?.getAttribute('data-domain-wide') === 'true';
        void confirm({
            title: 'Unbind application',
            html: unbindWarning(isLast, domainWide),
            confirmText: 'Unbind',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `SamlPeer/${peerId}/Application/${applicationId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to unbind application',
                    onSuccess: data => {
                        const nowhere = data['boundCount'] === 0 && data['domainWide'] !== true;
                        toast(nowhere ? 'warning' : 'success', 'Application unbound',
                            nowhere ? 'This peer is now offered nowhere. Bind an application or flag it domain-wide.' : '');
                        reloadDialog(peerId);
                    },
                    onFailure: failToast('Failed to unbind application'),
                });
            },
        });
    },

    /**
     * Attribute mapping add. Per-row, no batch save: each change is auditable
     * on its own and the row is durable as soon as the request returns.
     *
     * The two target fields are picker-BACKED but editable -- a peer may assert
     * vocabulary this instance does not model yet -- so the value is read from
     * the visible input, not from the picker's hidden id.
     */
    AddMap(): void {
        const table = one('#SamlPeerAttributeMapTable');
        const peerId = table?.getAttribute('data-peer-id');
        if (!peerId) return;
        const read = (id: string): string => ((one(`#${id}`) as HTMLInputElement | null)?.value ?? '').trim();
        const sourceAttribute = read('SamlPeerMapSource');
        const targetClaimType = read('SamlPeerMapTarget');
        if (!sourceAttribute || !targetClaimType) {
            toast('danger', 'Source attribute and target claim type are both required.');
            return;
        }
        void ajaxJson({
            url: `SamlPeer/${peerId}/AttributeMap`,
            method: 'POST',
            data: {
                PeerId: peerId,
                SourceAttribute: sourceAttribute,
                TargetClaimType: targetClaimType,
                TargetClaimValueType: read('SamlPeerMapValueType'),
            },
            errorTitle: 'Failed to add mapping',
            onSuccess: () => {
                toast('success', 'Attribute mapping added');
                // Re-fetch rather than append: the new row carries audit
                // information only the server has.
                reloadDialog(peerId);
            },
            onFailure: failToast('Failed to add mapping'),
        });
    },

    DeleteMap(mapId: number | string): void {
        void confirm({
            title: 'Remove mapping',
            message: 'Remove this attribute mapping? Future SAML sign-ins will not seed this claim. '
                + 'PersonClaims already written from it are left alone.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `SamlPeer/AttributeMap/${mapId}`,
                    method: 'DELETE',
                    errorTitle: 'Failed to remove mapping',
                    onSuccess: () => {
                        toast('success', 'Mapping removed');
                        removeMapRow(mapId);
                    },
                    onFailure: failToast('Failed to remove mapping'),
                });
            },
        });
    },
};

// ---------------------------------------------------------------- helpers

/**
 * Re-fetch the open dialog after a per-row write, and remember that the list
 * behind it is now out of date -- its "Offered to" badge counts bindings.
 */
function reloadDialog(peerId: number | string): void {
    listDirty = true;
    void ajaxModal({
        modal: MODAL,
        url: `SamlPeer/Detail/${peerId}`,
        title: null,
        formId: EDIT_FORM,
        errorTitle: 'Failed to reload peer',
    });
}

/**
 * What removing THIS binding actually does, which is the only thing the dialog
 * is for. Three outcomes, and the dangerous one is the middle: with the
 * domain-wide flag off, removing the LAST binding leaves the peer offered
 * nowhere rather than everywhere. The domain-wide case is checked first
 * because it makes the last-binding case moot -- reach does not change either
 * way, so saying "offered nowhere" there would be a lie.
 */
export function unbindWarning(isLast: boolean, domainWide: boolean): string {
    if (domainWide) {
        return 'Remove this application binding?<br/><br/>'
            + 'This peer is flagged domain-wide, so its reach does not change until you untick that box.';
    }
    if (isLast) {
        return 'Remove the last application binding?<br/><br/>'
            + '<span class="text-danger">This peer is then offered <strong>nowhere</strong> — no sign-in page '
            + 'shows it and any assertion it sends is rejected.</span>';
    }
    return 'Remove this application binding? This peer no longer appears on that application\'s sign-in page.';
}

/** Drop one mapping row, restoring the empty state when it was the last. */
export function removeMapRow(mapId: number | string): void {
    const table = one('#SamlPeerAttributeMapTable');
    if (!table) return;
    table.querySelector(`tr[data-map-id="${CSS.escape(String(mapId))}"]`)?.remove();
    if (table.querySelectorAll('tbody tr[data-map-id]').length > 0) return;
    const addRow = table.querySelector('#SamlPeerAttributeMapAddRow');
    if (!addRow) return;
    const empty = document.createElement('tr');
    empty.id = 'SamlPeerAttributeMapEmpty';
    const cell = document.createElement('td');
    cell.colSpan = 4;
    cell.className = 'text-secondary fst-italic';
    cell.textContent = 'No mappings configured. Asserted attributes will not seed PersonClaims.';
    empty.appendChild(cell);
    addRow.before(empty);
}

/**
 * The save button's icon swap while a write is in flight.
 *
 * Scoped to the MODAL, not to the form: the Create dialog's button lives in the
 * modal footer, outside `<form id="SamlPeerCreateForm">`, so the legacy
 * `$('#SamlPeerCreateForm .fa-save')` matched nothing and the spinner never
 * appeared on any create.
 */
function saving(): (working: boolean) => void {
    return working => {
        const modal = one(`#${MODAL}`);
        if (!modal) return;
        for (const node of Array.from(modal.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
        for (const node of Array.from(modal.querySelectorAll('.fa-spinner'))) node.classList.toggle('d-none', !working);
    };
}
